// /academy/onchain — Intro to on-chain analytics with curated metrics.
// Live widget for Fear & Greed Index (free public API). Other metrics get
// a static interpretation table.
import React, { useEffect, useState } from "react";
import { Network, ExternalLink } from "lucide-react";
import AcademyShell from "../components/academy/AcademyShell";
import { useLanguage } from "../i18n/LanguageContext";
import { getAcademyToolsI18n } from "../i18n/academy-tools";
import {
  getMetricasOnchain,
  COLOR_DOT,
  DIFICULTAD_COLOR,
  getRecursosExternos,
} from "../data/i18n-academy";

// ── Live data widget: Fear & Greed ─────────────────────────────────
const FearGreedWidget = ({ T }) => {
  const [val, setVal] = useState(null);
  const [label, setLabel] = useState("");
  const [err, setErr] = useState(false);

  useEffect(() => {
    let active = true;
    fetch("https://api.alternative.me/fng/?limit=1")
      .then((r) => r.json())
      .then((d) => {
        if (!active) return;
        const v = d?.data?.[0];
        if (v) {
          setVal(parseInt(v.value, 10));
          setLabel(v.value_classification);
        } else {
          setErr(true);
        }
      })
      .catch(() => active && setErr(true));
    return () => {
      active = false;
    };
  }, []);

  if (err) return null;

  const color =
    val === null
      ? "bg-white/5"
      : val <= 25
      ? "bg-emerald-500/15 text-emerald-200 border-emerald-400/30"
      : val <= 45
      ? "bg-lime-500/15 text-lime-200 border-lime-400/30"
      : val <= 55
      ? "bg-yellow-500/15 text-yellow-200 border-yellow-400/30"
      : val <= 75
      ? "bg-orange-500/15 text-orange-200 border-orange-400/30"
      : "bg-red-500/15 text-red-200 border-red-400/30";

  return (
    <div
      className={`mt-3 inline-flex items-center gap-3 rounded-lg border px-3 py-1.5 ${color}`}
      data-testid="onchain-fng-live"
    >
      <span className="text-[10px] font-bold uppercase tracking-[0.22em] opacity-75">
        {T.och_now}
      </span>
      <span className="font-mono text-xl font-black">{val ?? "…"}</span>
      <span className="text-[11px] font-bold uppercase tracking-[0.15em]">{label}</span>
    </div>
  );
};

const MetricaCard = ({ m, T }) => (
  <article
    className="rounded-xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-xl"
    data-testid={`onchain-metrica-${m.id}`}
  >
    <div className="mb-2 flex flex-wrap items-center gap-2">
      <span
        className={`rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.15em] ${DIFICULTAD_COLOR[m.dificultad]}`}
      >
        {T.och_diff[m.dificultad] || m.dificultad}
      </span>
      {m.apiUrl && (
        <span className="rounded-full border border-cyan-400/30 bg-cyan-400/[0.08] px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.15em] text-cyan-200">
          {T.och_live}
        </span>
      )}
    </div>
    <h3 className="font-display text-lg font-black text-white">{m.nombre}</h3>
    <p className="mt-2 text-[13px] leading-relaxed text-white/65">{m.descripcion}</p>

    {m.id === "fear-greed" && <FearGreedWidget T={T} />}

    <div className="mt-4 overflow-hidden rounded-lg border border-white/10">
      <table className="w-full text-left text-[12px]">
        <thead className="bg-white/[0.04] text-[10px] uppercase tracking-[0.18em] text-white/45">
          <tr>
            <th className="px-3 py-2 font-bold">{T.och_th_range}</th>
            <th className="px-3 py-2 font-bold">{T.och_th_signal}</th>
            <th className="px-3 py-2 font-bold">{T.och_th_read}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5 text-white/65">
          {m.interpretacion.map((row, i) => (
            <tr key={i} className="align-top">
              <td className="px-3 py-2 font-mono text-white/85">{row.rango}</td>
              <td className="px-3 py-2">
                <span className="inline-flex items-center gap-1.5">
                  <span className={`h-2 w-2 rounded-full ${COLOR_DOT[row.color]}`} />
                  {row.senal}
                </span>
              </td>
              <td className="px-3 py-2">{row.descripcion}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </article>
);

const AcademyOnchainPage = () => {
  const { lang } = useLanguage();
  const T = getAcademyToolsI18n(lang);
  const METRICAS_ONCHAIN = getMetricasOnchain(lang);
  const RECURSOS_EXTERNOS = getRecursosExternos(lang);
  return (
    <AcademyShell
      testid="academy-onchain"
      eyebrow={T.och_eyebrow}
      eyebrowIcon={Network}
      title={T.och_title}
      subtitle={T.och_subtitle}
      breadcrumbCurrent={T.och_breadcrumb}
      footHint={`${METRICAS_ONCHAIN.length} ${T.och_foot}`}
    >
      {/* INTRO */}
      <section className="mb-8 rounded-2xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur-xl">
        <h3 className="mb-3 text-xs font-black uppercase tracking-[0.25em] text-orange-300">
          {T.och_intro_title}
        </h3>
        <p className="text-[13px] leading-relaxed text-white/70">{T.och_intro_p1}</p>
        <p className="mt-3 text-[13px] leading-relaxed text-white/70">{T.och_intro_p2}</p>
        <p className="mt-3 text-[12px] italic text-white/40">{T.och_intro_limit}</p>
      </section>

      {/* METRICS GRID */}
      <div className="grid gap-5 lg:grid-cols-2" data-testid="onchain-grid">
        {METRICAS_ONCHAIN.map((m) => (
          <MetricaCard key={m.id} m={m} T={T} />
        ))}
      </div>

      {/* RESOURCES */}
      <section className="mt-10 rounded-2xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur-xl">
        <h3 className="mb-4 text-xs font-black uppercase tracking-[0.25em] text-orange-300">
          {T.och_resources}
        </h3>
        <div className="grid gap-3 sm:grid-cols-3">
          {RECURSOS_EXTERNOS.map((r) => (
            <a
              key={r.url}
              href={r.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex flex-col rounded-xl border border-white/10 bg-black/40 p-4 transition-all hover:border-orange-400/40"
            >
              <span className="mb-1 inline-flex items-center gap-1.5 font-display text-base font-black text-white">
                {r.nombre}
                <ExternalLink className="h-3 w-3 text-white/40 transition-colors group-hover:text-orange-300" />
              </span>
              <span className="text-[12px] text-white/55">{r.desc}</span>
            </a>
          ))}
        </div>
      </section>
    </AcademyShell>
  );
};

export default AcademyOnchainPage;
